import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, CircleDashed, Loader2, X } from 'lucide-react';
import { addDevice, loadDeviceTypes, loadDevices, printTestLabel, saveLocationWifi, startPrinterPairing } from './api';
import { Code128, DeviceDrawing } from './DeviceDrawings';
import { hasTx, tx } from './i18n';
import { errorMessage } from './ui';
import { bluetoothAvailable, connectPrinter, networkNameFits, passwordFits, setupCommands, wifiKey } from './zebraSetup';

const text = (key, values) => tx(`ui.devices.wizard.${key}`, values);
const typeNote = type => (hasTx(`ui.devices.type.${type.code}`) ? tx(`ui.devices.type.${type.code}`) : tx(`ui.devices.purpose.${type.purpose}`));
// The barcode a scanner is tried with. It has the dash of a tube code, the one
// character a keyboard language can change, and is no real tube.
export const TEST_CODE = '000000-00';
const PRINTER_STEPS = ['prepare', 'find', 'wifi', 'progress', 'done'];
const SCANNER_STEPS = ['plug', 'enter', 'test', 'done'];
const ONLINE_POLL_MS = 3000;
const ONLINE_WAIT_MS = 150000;
const wait = ms => new Promise(resolve => window.setTimeout(resolve, ms));

// What a test scan showed: whether Enter arrived, and whether the code came
// through as printed.
export function scanVerdict(typed, entered) {
  return { entered, exact: typed === TEST_CODE, typed };
}

function Steps({ count, current }) {
  return <ol className="xo-dev-steps" aria-hidden="true">{Array.from({ length: count }, (_, index) => (
    <li key={index} className={index < current ? 'is-done' : index === current ? 'is-current' : ''} />
  ))}</ol>;
}

function ChooseType({ onChosen, busy }) {
  const [types, setTypes] = useState(null), [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    loadDeviceTypes().then(value => { if (live) setTypes(value); }, caught => { if (live) setError(errorMessage(caught)); });
    return () => { live = false; };
  }, []);
  if (error !== '') return <p className="xo-alert t-bad" role="alert">{error}</p>;
  if (types === null) return <p className="xo-dev-wait"><Loader2 className="xo-spin" aria-hidden="true" />{tx('ui.parts.loading')}</p>;
  return <>
    <h3>{text('choose')}</h3>
    <ul className="xo-dev-types">{types.map(type => <li key={type.code}>
      <button type="button" onClick={() => onChosen(type)} disabled={busy}>
        <DeviceDrawing category={type.category} purpose={type.purpose} />
        <strong>{type.model}</strong><small>{typeNote(type)}</small>
      </button>
    </li>)}</ul>
  </>;
}

function PrinterSteps({ device, onChanged, onFinished, setCount, setCurrent }) {
  const [step, setStep] = useState('prepare');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [found, setFound] = useState(null), [pairing, setPairing] = useState(null);
  const [useSaved, setUseSaved] = useState(true), [networkName, setNetworkName] = useState(''), [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true), [progress, setProgress] = useState(0), [failed, setFailed] = useState(false);
  const printer = useRef(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; if (printer.current) printer.current.disconnect(); }; }, []);
  useEffect(() => { setCount(PRINTER_STEPS.length); setCurrent(PRINTER_STEPS.indexOf(step)); }, [step, setCount, setCurrent]);

  async function find() {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const line = await connectPrinter();
      printer.current = line;
      let serial = '';
      try { serial = await line.ask('device.unique_id'); } catch (caught) { serial = ''; }
      const started = await startPrinterPairing(device.deviceId);
      if (!alive.current) return;
      setFound({ name: line.name, serial }); setPairing(started);
      setUseSaved(started.wifi !== null); setNetworkName(''); setPassword('');
      setStep('wifi');
    } catch (caught) {
      // Closing the browser's device list without choosing is not an error to explain.
      if (caught && caught.name === 'NotFoundError') setError(text('find_none'));
      else if (caught && (caught.name === 'NetworkError' || caught.message === 'PRINTER_SILENT')) setError(text('find_failed'));
      else setError(errorMessage(caught));
    } finally { if (alive.current) setBusy(false); }
  }

  async function connect() {
    if (busy) return;
    const saved = pairing.wifi !== null && useSaved;
    if (!saved && !networkNameFits(networkName)) { setError(text('wifi_name_invalid')); return; }
    if (!saved && !passwordFits(password)) { setError(text('wifi_password_invalid')); return; }
    setBusy(true); setError(''); setFailed(false); setProgress(0); setStep('progress');
    try {
      const network = saved ? pairing.wifi.networkName : networkName;
      const key = saved ? pairing.wifi.key : await wifiKey(networkName, password);
      for (const command of setupCommands({ networkName: network, key, address: pairing.address, certificates: pairing.certificates, purpose: pairing.purpose })) {
        await printer.current.send(command);
      }
      if (!alive.current) return;
      setProgress(1);
      const deadline = Date.now() + ONLINE_WAIT_MS;
      let online = false;
      while (alive.current && !online && Date.now() < deadline) {
        await wait(ONLINE_POLL_MS);
        const now = (await loadDevices()).devices.find(row => row.deviceId === device.deviceId);
        if (now && now.online) setProgress(2);
        online = Boolean(now && now.online && now.ready);
      }
      if (!alive.current) return;
      if (!online) { setFailed(true); return; }
      setProgress(3);
      // The Wi-Fi is kept only once it has proven to work.
      if (!saved && remember && pairing.wifiCanBeSaved) await saveLocationWifi(network, key);
      await printTestLabel(device.deviceId);
      await onChanged();
      if (alive.current) setStep('done');
    } catch (caught) {
      if (alive.current) { setError(errorMessage(caught)); setFailed(true); }
    } finally { if (alive.current) setBusy(false); }
  }

  if (step === 'prepare') return <>
    <div className="xo-dev-wizard-art"><DeviceDrawing category="printer" purpose={device.type.purpose} /></div>
    <h3>{text('prepare_title')}</h3>
    <ol className="xo-dev-checklist">
      <li>{text('prepare_power')}</li><li>{text('prepare_labels')}</li>
      {device.type.purpose === 'specimen_labels' && <li>{text('prepare_ribbon')}</li>}
      <li>{text('prepare_near')}</li>
    </ol>
    <footer><button type="button" className="xo-btn" onClick={() => setStep('find')}>{text('next')}</button></footer>
  </>;
  if (step === 'find') return <>
    <h3>{text('find_title')}</h3>
    {bluetoothAvailable() ? <>
      <p>{text('find_text')}</p>
      {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
      <footer><button type="button" className="xo-btn-2" onClick={() => { setError(''); setStep('prepare'); }} disabled={busy}>{text('back')}</button>
        <button type="button" className="xo-btn" onClick={find} disabled={busy}>{text(busy ? 'finding' : 'find_button')}</button></footer>
    </> : <>
      <p className="xo-alert t-info"><strong>{text('no_bluetooth_title')}</strong> {text('no_bluetooth_text', { code: device.deviceId })}</p>
      <footer><button type="button" className="xo-btn-2" onClick={() => setStep('prepare')}>{text('back')}</button>
        <button type="button" className="xo-btn" onClick={onFinished}>{text('close')}</button></footer>
    </>}
  </>;
  if (step === 'wifi') return <>
    <h3>{text('wifi_title')}</h3>
    {found && <p className="xo-dev-found"><Check aria-hidden="true" />{text('found', { name: found.name || device.type.model, serial: found.serial || device.deviceId })}</p>}
    <p>{text('wifi_text')}</p>
    {pairing.wifi !== null && <div className="xo-dev-choice" role="radiogroup" aria-label={text('wifi_title')}>
      <label><input type="radio" name="xo-dev-wifi" checked={useSaved} onChange={() => setUseSaved(true)} /><span>{text('wifi_saved_option', { name: pairing.wifi.networkName })}</span></label>
      <label><input type="radio" name="xo-dev-wifi" checked={!useSaved} onChange={() => setUseSaved(false)} /><span>{text('wifi_other')}</span></label>
    </div>}
    {(pairing.wifi === null || !useSaved) && <div className="xo-dev-fields">
      <label><span>{text('wifi_name')}</span><input value={networkName} onChange={event => setNetworkName(event.target.value)} maxLength={32} autoComplete="off" spellCheck="false" /></label>
      <label><span>{text('wifi_password')}</span><input type="password" value={password} onChange={event => setPassword(event.target.value)} maxLength={63} autoComplete="off" /></label>
      {pairing.wifiCanBeSaved && <label className="xo-dev-check"><input type="checkbox" checked={remember} onChange={event => setRemember(event.target.checked)} /><span>{text('wifi_save')}</span></label>}
    </div>}
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <footer><button type="button" className="xo-btn" onClick={connect} disabled={busy}>{text('connect_button')}</button></footer>
  </>;
  if (step === 'progress') return <>
    <h3>{text(failed ? 'timeout_title' : 'progress_title')}</h3>
    <ol className="xo-dev-progress">{['progress_send', 'progress_restart', 'progress_online', 'progress_label'].map((key, index) => (
      <li key={key} className={index < progress ? 'is-done' : index === progress && !failed ? 'is-current' : ''}>
        {index < progress ? <Check aria-hidden="true" /> : index === progress && !failed ? <Loader2 className="xo-spin" aria-hidden="true" /> : <CircleDashed aria-hidden="true" />}
        {text(key)}
      </li>))}</ol>
    {failed && <p className="xo-alert t-bad" role="alert">{error !== '' ? error : text('timeout_text')}</p>}
    {failed && <footer><button type="button" className="xo-btn-2" onClick={onFinished}>{text('close')}</button>
      <button type="button" className="xo-btn" onClick={() => { setError(''); setFailed(false); setStep(printer.current ? 'wifi' : 'find'); }}>{text('try_again')}</button></footer>}
  </>;
  return <>
    <div className="xo-dev-wizard-art is-done"><DeviceDrawing category="printer" purpose={device.type.purpose} /></div>
    <h3>{text('done_printer_title')}</h3>
    <p>{text('done_printer_text')}</p>
    <footer><button type="button" className="xo-btn" onClick={onFinished}>{text('done')}</button></footer>
  </>;
}

function ScannerSteps({ device, onFinished, setCount, setCurrent }) {
  const [step, setStep] = useState('plug');
  const [typed, setTyped] = useState(''), [verdict, setVerdict] = useState(null);
  const field = useRef(null);
  useEffect(() => { setCount(SCANNER_STEPS.length); setCurrent(SCANNER_STEPS.indexOf(step)); }, [step, setCount, setCurrent]);
  useEffect(() => { if (step === 'test' && field.current) field.current.focus(); }, [step, verdict]);
  // A scanner types a whole code within a moment. If the typing stops and no
  // Enter came, that is the answer: the scanner does not send it.
  useEffect(() => {
    if (step !== 'test' || typed === '') return undefined;
    const timer = window.setTimeout(() => { setVerdict(scanVerdict(typed, false)); setTyped(''); }, 1500);
    return () => window.clearTimeout(timer);
  }, [step, typed]);

  if (step === 'plug') return <>
    <div className="xo-dev-wizard-art"><DeviceDrawing category="scanner" /></div>
    <h3>{text('scanner_plug_title')}</h3>
    <ol className="xo-dev-checklist"><li>{text('scanner_plug_1')}</li><li>{text('scanner_plug_2')}</li></ol>
    <footer><button type="button" className="xo-btn" onClick={() => setStep('enter')}>{text('next')}</button></footer>
  </>;
  if (step === 'enter') return <>
    <h3>{text('scanner_enter_title')}</h3>
    <p>{text('scanner_enter_text')}</p>
    <footer><button type="button" className="xo-btn-2" onClick={() => setStep('plug')}>{text('back')}</button>
      <button type="button" className="xo-btn" onClick={() => setStep('test')}>{text('next')}</button></footer>
  </>;
  if (step === 'test') return <>
    <h3>{text('scanner_test_title')}</h3>
    <p>{text('scanner_test_text')}</p>
    <div className="xo-dev-scan-target"><Code128 value={TEST_CODE} label={TEST_CODE} /><code>{TEST_CODE}</code></div>
    <form className="xo-dev-fields" onSubmit={(event) => { event.preventDefault(); if (typed !== '') { setVerdict(scanVerdict(typed, true)); setTyped(''); } }}>
      <label><span>{text('scanner_test_field')}</span>
        <input ref={field} value={typed} onChange={event => { setVerdict(null); setTyped(event.target.value); }} maxLength={64} autoComplete="off" spellCheck="false" /></label>
    </form>
    {verdict && <ul className="xo-dev-verdict">
      <li className={verdict.entered ? 'is-ok' : 'is-bad'}>{verdict.entered ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
        {text(verdict.entered ? 'scanner_test_enter_ok' : 'scanner_test_enter_missing')}</li>
      <li className={verdict.exact ? 'is-ok' : 'is-bad'}>{verdict.exact ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
        {verdict.exact ? text('scanner_test_code_ok') : text('scanner_test_code_changed', { value: verdict.typed })}</li>
    </ul>}
    <footer><button type="button" className="xo-btn-2" onClick={() => setStep('enter')}>{text('back')}</button>
      <button type="button" className="xo-btn" onClick={() => setStep('done')} disabled={!verdict || !verdict.entered}>{text('next')}</button></footer>
  </>;
  return <>
    <div className="xo-dev-wizard-art is-done"><DeviceDrawing category="scanner" /></div>
    <h3>{text('scanner_done_title')}</h3>
    <p>{text('scanner_done_text', { code: device.deviceId })}</p>
    <footer><button type="button" className="xo-btn" onClick={onFinished}>{text('done')}</button></footer>
  </>;
}

// Adds a device at the signed-in account's own location and walks through
// setting it up: a printer is handed its Wi-Fi and our address over Bluetooth
// and then connects by itself; a scanner is plugged in and tried.
export default function DeviceWizard({ start, onChanged, onClose }) {
  const ref = useRef(null);
  const titleId = useId();
  const [device, setDevice] = useState(start.device || null);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [count, setCount] = useState(0), [current, setCurrent] = useState(0);
  useEffect(() => {
    const dialog = ref.current;
    const trigger = document.activeElement;
    dialog.showModal();
    return () => { if (dialog.open) dialog.close(); if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus(); };
  }, []);
  async function choose(type) {
    if (busy) return;
    setBusy(true); setError('');
    try { const added = await addDevice(type.code); await onChanged(); setDevice(added); }
    catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  }
  const printer = device !== null && device.type.category === 'printer';
  const title = device === null ? text('title_add') : text(printer ? 'title_printer' : 'title_scanner');
  return <dialog ref={ref} className="xo-dev-wizard" aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); onClose(); }}>
    <header>
      <div><small>{device === null ? '' : `${device.type.model} · ${device.deviceId}`}</small><h2 id={titleId}>{title}</h2></div>
      <button type="button" className="xo-icon-btn" aria-label={text('close')} onClick={onClose}><X aria-hidden="true" /></button>
    </header>
    {device !== null && count > 0 && <Steps count={count} current={current} />}
    <div className="xo-dev-wizard-body">
      {device === null && <ChooseType onChosen={choose} busy={busy} />}
      {device === null && error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
      {device !== null && printer && <PrinterSteps device={device} onChanged={onChanged} onFinished={onClose} setCount={setCount} setCurrent={setCurrent} />}
      {device !== null && !printer && <ScannerSteps device={device} onFinished={onClose} setCount={setCount} setCurrent={setCurrent} />}
    </div>
  </dialog>;
}
